import { useState } from 'react';
import Button from 'src/components/buttons/button';
import config from 'config/config';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}lower-your-cholesterol/`;

const getMenu = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li>
        <div className="navigator__contents__number">1.</div>
        <div className="navigator__contents__content-text">
          <a href="#1">What is risk?</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">2.</div>
        <div className="navigator__contents__content-text">
          <a href="#2">How do I know I have high cholesterol?</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">3.</div>
        <div className="navigator__contents__content-text">
          <a href="#3">What should my cholesterol levels be?</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">4.</div>
        <div className="navigator__contents__content-text">
          <a href="#4">What does cholesterol do to your heart?</a>
        </div>
      </li>
    </ul>
  </section>
);

const getPage = () => (
  <div className="navigator">
    <section className="navigator__content">
      <h1 className="navigator__title">{'Week 2: What you need to know about cholesterol'}</h1>
      <p>
        {`Understanding what 'high' cholesterol means for your health is the first step to lowering your risk of
        developing heart and circulatory diseases.`}
      </p>
      <p>
        Focussing on your overall risk of heart and circulatory diseases is important. When you have your cholesterol
        checked, your doctor will probably still give you specific readings for your good and bad cholesterol. They
        will also consider how this fits into your overall risk, including your lifestyle and family history, and this
        can help them to decide on your treatment options.
      </p>
      <div className="visible-xs">{getMenu()}</div>

      <h2>What is risk?</h2>
      <p>
        {`When you're diagnosed with high cholesterol, you'll hear a lot of people talking about risk. There are different
        types of risk, and a lot of different factors your doctor or nurse will consider before telling you what your
        risk is.`}
      </p>
      <p>Taking your risk seriously is vital to help prevent heart and circulatory diseases.</p>
      <div className="navigator__video-container">
        <iframe
          allow="autoplay; encrypted-media; fullscreen"
          src="https://www.youtube.com/embed/GzawkcAfEPQ"
        />
      </div>

      <h2>How do I know I have high cholesterol?</h2>
      <p>
        High cholesterol is diagnosed through a blood test. Your doctor or nurse will tell you if your cholesterol
        reading puts you at high, moderate or low risk of developing heart and circulatory diseases in the next 10 years
      </p>
      <p>
        {`This risk isn't just about the numbers when your cholesterol is measured. It also takes into account your family
        history, your Body Mass Index (BMI), and other risk factors you may have like high blood pressure or diabetes.`}
      </p>
      <p>
        {`When fatty material builds up in arteries, it's called atherosclerosis. It can affect you in several different
        ways, including causing a heart attack or stroke.`}
      </p>
      <div className="navigator__video-container">
        <iframe
          allow="autoplay; encrypted-media; fullscreen"
          src="https://www.youtube.com/embed/y6QJceOAVY0"
        />
      </div>

      <h2>What should my cholesterol levels be?</h2>
      <p>
        {`Doctors no longer work towards a 'healthy' number for cholesterol readings that suits everyone. This is because
        your overall risk will determine what your ideal cholesterol levels should be.`}
      </p>
      <p>
        Your doctor or nurse will explain to you what level you should aim for. This takes into account your risk
        factors and will tailor your treatment to your needs.
      </p>
      <div className="navigator__video-container">
        <iframe
          allow="autoplay; encrypted-media; fullscreen"
          src="https://www.youtube.com/embed/yu8_TdLGG0c"
        />
      </div>

      <h2>What does cholesterol do to your heart?</h2>
      <p>
        {`High cholesterol often doesn't have any symptoms until it causes serious problems. We've teamed up with NHS
        Choices to create the Heart Age Tool to help you check your risk of heart and circulatory diseases.`}
      </p>
      <p>Find out the impact your cholesterol levels could be having on your heart - and what to do about it.</p>
      <a
        href="https://www.bhf.org.uk/heart-health/risk-factors/check-your-heart-age"
        rel="noreferrer"
        target="_blank"
      >
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Find out your heart age"
        />
      </a>

      <img alt="Content provided by BHF" className="navigator__image-full navigator__banner-footer" src={`${PROGRAM_URL}bhf-content.jpg`} />
    </section>
  </div>
);

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <img alt="help line call to action" className="navigator__footer__image" src={`${PROGRAM_URL}cta-helpline.png`} />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">Call our Heart Helpline</div>
        <div className="navigator__footer__description">
          Our Heart Helpline has Cardiac Nurses available to answer your questions. Call us on 0300 330 3311 or email{' '}
          <a
            className="navigator__footer__link"
            href="mailto:hearthelpline@bhf.org.uk"
          >
            hearthelpline@bhf.org.uk
          </a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Lower your cholesterol | HealthUnlocked',
};

export default obj;
